import { useRef } from "react";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { LINKS } from "@/lib/site";
import "./Testimonials.css";

const testimonials = [
  {
    quote:
      "I had the pleasure of designating Hamza as project manager for one of our growth strategy campaigns, which led to fantastic returns and opened up many new jobs as a result of the project's success.",
    name: "Nish Ithayakumar",
    role: "Sr. Director of Business Operations @ Cerby",
  },
  {
    quote:
      "He is always coming up with new ideas and enthusiastic in the process - a problem solver to his core. I owe many of my successes to him.",
    name: "Lorenzo Walker",
    role: "License Manager @ Unity",
  },
  {
    quote:
      "Hamza's out of the box thinking has had a direct impact on how we approach customers with various sales plays.",
    name: "Ryan Tiller",
    role: "Senior Client Partner @ Unity",
  },
  {
    quote:
      "Hamza has developed several different models which enabled the productivity of the sales team and data-driven decisions. Those contributions were invaluable to the entire collective and have led us to immense success.",
    name: "Ruoyao (Kevin) Liu",
    role: "Growth @ Unity",
  },
  {
    quote:
      "Hamza played such a massive role in my success at Unity by always suggesting new ideas and sales strategies.",
    name: "Charles Renverse",
    role: "Colleague @ Unity",
  },
  {
    quote:
      "He excelled in streamlining complex datasets, creating engaging video content, and providing great sales training.",
    name: "Dare Fajinmi",
    role: "Enterprise Account Manager @ PayPal",
  },
];

const leftStack = testimonials.filter((_, i) => i % 2 === 0);
const rightStack = testimonials.filter((_, i) => i % 2 === 1);

const accents = [
  { bar: "bg-cobalt", ink: false },
  { bar: "bg-flame", ink: true },
  { bar: "bg-cobalt", ink: false },
];

const StackCard = ({ t, index, total, number, grandTotal, stackOffset, reduceMotion, accent, side }) => {
  const ref = useRef(null);
  const dealFrom = side === "left" ? -72 : 72;

  // 0 enter → ~0.4 locked on top → 1 tucked under the next card in the deck
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "start 0.22", "end start"],
  });

  const scale = useTransform(
    scrollYProgress,
    [0, 0.38, 0.72, 1],
    reduceMotion ? [1, 1, 1, 1] : [0.86, 1, 0.97, 0.93]
  );
  const y = useTransform(
    scrollYProgress,
    [0, 0.38, 0.72, 1],
    reduceMotion ? [0, 0, 0, 0] : [90, 0, -10, -28]
  );
  const x = useTransform(
    scrollYProgress,
    [0, 0.38, 1],
    reduceMotion ? [0, 0, 0] : [dealFrom, 0, 0]
  );
  const rotateX = useTransform(
    scrollYProgress,
    [0, 0.38, 0.72, 1],
    reduceMotion ? [0, 0, 0, 0] : [28, 0, -3, -8]
  );
  const rotateZ = useTransform(
    scrollYProgress,
    [0, 0.38, 1],
    reduceMotion ? [0, 0, 0] : [side === "left" ? -5 : 5, 0, side === "left" ? -0.6 : 0.6]
  );
  const opacity = useTransform(
    scrollYProgress,
    [0, 0.18, 0.85, 1],
    reduceMotion ? [1, 1, 1, 1] : [0.2, 1, 1, 0.92]
  );

  return (
    <div
      ref={ref}
      className="testimonial-stack-card sticky top-20 md:top-28"
      style={{
        zIndex: index + 1,
        paddingBottom: (total - 1 - index) * stackOffset,
        perspective: 1400,
      }}
      data-testid={`testimonial-${t.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
    >
      <motion.article
        style={{
          scale,
          y,
          x,
          rotateX,
          rotateZ,
          opacity,
          transformPerspective: 1400,
          transformStyle: "preserve-3d",
        }}
        className={`relative overflow-hidden border border-ink shadow-hard will-change-transform origin-bottom ${
          accent.ink ? "bg-ink text-paper" : "bg-paper text-ink"
        }`}
      >
        <div className={`h-2 w-full ${accent.bar}`} />
        <div className="p-6 md:p-8">
          <div className="flex items-start justify-between gap-4 mb-5">
            <span
              className={`font-editorial italic text-6xl md:text-7xl leading-none select-none ${
                accent.ink ? "text-cobalt" : "text-cobalt"
              }`}
              aria-hidden
            >
              &ldquo;
            </span>
            <span
              className={`font-mono text-base uppercase tracking-[0.12em] tabular-nums ${
                accent.ink ? "text-white" : "text-ink"
              }`}
            >
              {String(number).padStart(2, "0")} / {String(grandTotal).padStart(2, "0")}
            </span>
          </div>

          <blockquote
            className={`text-lg md:text-xl font-light leading-snug ${
              accent.ink ? "text-white" : "text-ink"
            }`}
          >
            {t.quote}
          </blockquote>

          <footer
            className={`mt-7 pt-5 border-t ${accent.ink ? "border-white/20" : "border-ink/15"}`}
          >
            <span className="block font-display font-semibold text-xl md:text-2xl tracking-tight">
              {t.name}
            </span>
            <span
              className={`mt-1.5 block text-base md:text-lg leading-snug ${
                accent.ink ? "text-paper" : "text-ink"
              }`}
            >
              {t.role}
            </span>
          </footer>
        </div>
      </motion.article>
    </div>
  );
};

const StackColumn = ({ items, reduceMotion, side, offsetStart = 0, numberOffset = 0, grandTotal }) => (
  <div className="relative" style={{ perspective: 1400 }}>
    {items.map((t, i) => (
      <StackCard
        key={t.name}
        t={t}
        index={i}
        total={items.length}
        number={numberOffset + i + 1}
        grandTotal={grandTotal}
        stackOffset={26}
        reduceMotion={reduceMotion}
        accent={accents[(i + offsetStart) % accents.length]}
        side={side}
      />
    ))}
  </div>
);

export const Testimonials = () => {
  const reduceMotion = useReducedMotion();
  const grandTotal = testimonials.length;

  return (
    <section id="testimonials" className="bg-paper border-t border-ink" data-testid="testimonials-section">
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 pt-10 md:pt-14 pb-4">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-8 md:mb-12">
          <div className="max-w-3xl">
            <h2 className="font-display font-semibold tracking-[-0.02em] text-4xl md:text-5xl lg:text-6xl leading-[0.95] text-ink text-balance">
              The{" "}
              <span className="font-editorial italic font-medium text-cobalt">testimonials.</span>
            </h2>
            <p className="mt-4 text-lg md:text-xl font-light text-ink leading-snug max-w-xl text-pretty">
              Excerpts from Hamza's LinkedIn recommendations.
            </p>
          </div>
          <a
            href={LINKS.linkedin}
            target="_blank"
            rel="noopener noreferrer"
            className="group inline-flex w-fit items-center gap-2 bg-cobalt px-6 py-3.5 font-mono text-base font-medium uppercase tracking-[0.12em] text-white border border-ink transition-all duration-200 hover:-translate-y-1 hover:shadow-hard active:translate-y-0 active:shadow-none"
            data-testid="testimonials-linkedin"
          >
            See all on LinkedIn
            <ArrowUpRight
              size={18}
              className="transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
            />
          </a>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-5 md:px-10 pb-16 md:pb-24 grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-8 items-start">
        <StackColumn
          items={leftStack}
          reduceMotion={reduceMotion}
          side="left"
          offsetStart={0}
          numberOffset={0}
          grandTotal={grandTotal}
        />
        <div className="md:mt-20">
          <StackColumn
            items={rightStack}
            reduceMotion={reduceMotion}
            side="right"
            offsetStart={1}
            numberOffset={leftStack.length}
            grandTotal={grandTotal}
          />
        </div>
      </div>
    </section>
  );
};
